import {Component, Input, OnInit, QueryList, ViewChildren, ViewEncapsulation} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {StatisticsTypes} from '../../../../statistics/enums/statistics-types.enum';
import {ReportFilterType} from '../../../../statistics/enums/report-filter-type.enum';
import {OverTimeGraphType} from '../../../../statistics/enums/over-time-graph-type.enum';
import {DateType} from '../../../../statistics/enums/date-type.enum';
import {ReportIdentifier} from '../../../../statistics/enums/report-identifier.enum';
import {DateTimeUnitTypes} from '../../../../statistics/enums/date-time-unit-types.enum';
import {StatisticsApiService} from '../../../../services/api/statistics/statistics-api.service';
import {HighchartComponent} from '../../../../utils/highchart/highchart.component';
import {ChartParentType} from '../../../../statistics/enums/chart-parent-type.enum';
import {ChartType} from '../../../../utils/highchart/chart-type';
import {StatisticsTypesList} from '../../../../statistics/enums/statistics.constants';
import * as _ from 'lodash';
import {AcDialogService} from 'ac-infra';
import {ChartDialogComponent} from '../../../../dialogs/chart-dialog/chart-dialog.component';

@Component({
  selector: 'node-summary',
  templateUrl: './node-summary.component.html',
  styleUrls: ['./node-summary.component.less'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('openCloseNodeStates', [
      state('true', style({
        height: '*'
      })),
      state('false', style({
        height: '0px',
        visibility: 'hidden'
      })),
      transition('false <=> true', [
        animate('0.3s')
      ])
    ])
  ]
})
export class NodeSummaryComponent implements OnInit {

  @Input()
  set selection(selection: any) {
    this.dataInitialization(selection);
  }

  constructor(private statisticsApi: StatisticsApiService,
              private acDialogService: AcDialogService) {
  }

  routingAttemptsStatistics: any;
  unsuccessfulRoutesStatistics: any;
  callsStatistics: any;
  chartParentType: any;
  chartType: any;
  chartIsStack: any;
  isCollapsed = true;
  optionsForRoutingAttempts: any;
  optionsForUnsuccessfulRoutes: any;
  optionsForCalls: any;
  node: any;

  routingAttemptsChart: HighchartComponent;
  unsuccessfulRoutesChart: HighchartComponent;
  callsChart: HighchartComponent;

  @ViewChildren(HighchartComponent)
  charts: QueryList<HighchartComponent>;

  ngOnInit(): void {
    this.routingAttemptsStatistics = this.getRoutingAttemptsStatisticsNames();
    this.unsuccessfulRoutesStatistics = this.getUnsuccessfulRoutesStatisticsNames();
    this.callsStatistics = this.getCallsStatisticsNames();
    this.chartParentType = ChartParentType.LINE;
    this.chartType = ChartType[ChartParentType.LINE].type;
    this.chartIsStack = ChartType[ChartParentType.LINE].stack;

    this.optionsForRoutingAttempts = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Routing attempts per 5 minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };

    this.optionsForUnsuccessfulRoutes = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Unsuccessful Routes Per 5 Minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };

    this.optionsForCalls = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: 'Calls Per 5 Minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };
  }

  initializeCharts(): void {
    const chartArray = this.charts.toArray();
    this.routingAttemptsChart = chartArray[0];
    this.unsuccessfulRoutesChart = chartArray[1];
    this.callsChart = chartArray[2];
  }

  nodeOperativeStateToColor(status): any {
    switch (status) {
      case 0:
        return {c2n: 'red', n2c: 'red', n2rR: 'red', n2rN: 'red'};
      case 1:
        return {c2n: 'red', n2c: 'red', n2rR: 'red', n2rN: 'green'};
      case 2:
        return {c2n: 'red', n2c: 'red', n2rR: 'green', n2rN: 'red'};
      case 3:
        return {c2n: 'red', n2c: 'red', n2rR: 'green', n2rN: 'green'};
      case 4:
        return {c2n: 'red', n2c: 'green', n2rR: 'red', n2rN: 'red'};
      case 5:
        return {c2n: 'red', n2c: 'green', n2rR: 'red', n2rN: 'green'};
      case 6:
        return {c2n: 'red', n2c: 'green', n2rR: 'green', n2rN: 'red'};
      case 7:
        return {c2n: 'red', n2c: 'green', n2rR: 'green', n2rN: 'green'};
      case 8:
        return {c2n: 'green', n2c: 'red', n2rR: 'red', n2rN: 'red'};
      case 9:
        return {c2n: 'green', n2c: 'red', n2rR: 'red', n2rN: 'green'};
      case 10:
        return {c2n: 'green', n2c: 'red', n2rR: 'green', n2rN: 'red'};
      case 11:
        return {c2n: 'green', n2c: 'red', n2rR: 'green', n2rN: 'green'};
      case 12:
        return {c2n: 'green', n2c: 'green', n2rR: 'red', n2rN: 'red'};
      case 13:
        return {c2n: 'green', n2c: 'green', n2rR: 'red', n2rN: 'green'};
      case 14:
        return {c2n: 'green', n2c: 'green', n2rR: 'green', n2rN: 'red'};
      case 15:
        return {c2n: 'green', n2c: 'green', n2rR: 'green', n2rN: 'green'};

      case 94:
        return {c2n: 'red', n2c: 'red', n2rR: 'red', n2rN: 'red'};
      default:
        return {c2n: 'grey', n2c: 'grey', n2rR: 'grey', n2rN: 'grey'};
    }
  }

  openRoutingAttemptsChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForRoutingAttempts
    });
  }

  openUnsuccessfulRoutesChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForUnsuccessfulRoutes
    });
  }

  openCallsChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForCalls
    });
  }

  getChartsData(): void {
    if (this.node === undefined || this.node === null) {
      return;
    }

    const parameters = {
      reportIdentifier: ReportIdentifier.nodeOverTime,
      dateType: DateType.RELATIVE,
      relativeTimeUnit: DateTimeUnitTypes.HOUR,
      relativeTimeUnitValue: 3,
      overTimeGraphType: OverTimeGraphType.MULTIPLE_STATISTICS,
      availableFilters: [
        ReportFilterType.DATE,
        ReportFilterType.MULTIPLE_STATISTICS,
        ReportFilterType.ELEMENTS,
        ReportFilterType.OVER_TIME_GRAPH_TYPE
      ],
      multipleStatistics: [
        StatisticsTypes.ROUTING_ATTEMPTS,
        StatisticsTypes.UNSUCCESSFUL_ROUTES,
        StatisticsTypes.ALTERNATIVE_ATTEMPTS,
        StatisticsTypes.DESTINATION_NOT_ROUTABLE,
        StatisticsTypes.DESTINATION_CALL,
        StatisticsTypes.TRANSIENT_CALL
      ],
      elements: [this.node.id]
    };

    this.statisticsApi.getReportForChart(parameters)
      .then((reports => {
        this.initializeCharts();
        this.setRoutingAttemptsReport(reports);
        this.setUnsuccessfulRoutesReport(reports);
        this.setCallsReport(reports);
      }))
      .catch(() => {
      });
  }

  setRoutingAttemptsReport(value): void {
    const series = value.series.filter((element) => {
      return this.routingAttemptsStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForRoutingAttempts.series = series;
    this.optionsForRoutingAttempts.startDate = value.startDate;
    this.optionsForRoutingAttempts.interval = value.interval;

    this.routingAttemptsChart.setOptions(this.optionsForRoutingAttempts);
  }

  setUnsuccessfulRoutesReport(value): void {
    const series = value.series.filter((element) => {
      return this.unsuccessfulRoutesStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForUnsuccessfulRoutes.series = series;
    this.optionsForUnsuccessfulRoutes.startDate = value.startDate;
    this.optionsForUnsuccessfulRoutes.interval = value.interval;

    this.unsuccessfulRoutesChart.setOptions(this.optionsForUnsuccessfulRoutes);
  }

  setCallsReport(value): void {
    const series = value.series.filter((element) => {
      return this.callsStatistics.indexOf(element.name) !== -1;
    });

    this.optionsForCalls.series = series;
    this.optionsForCalls.startDate = value.startDate;
    this.optionsForCalls.interval = value.interval;

    this.callsChart.setOptions(this.optionsForCalls);
  }

  getRoutingAttemptsStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.ROUTING_ATTEMPTS;
    }).map((element) => {
      return element.name;
    });
  }

  getUnsuccessfulRoutesStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.UNSUCCESSFUL_ROUTES ||
        element.value === StatisticsTypes.ALTERNATIVE_ATTEMPTS ||
        element.value === StatisticsTypes.DESTINATION_NOT_ROUTABLE;
    }).map((element) => {
      return element.name;
    });
  }

  getCallsStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.DESTINATION_CALL ||
        element.value === StatisticsTypes.TRANSIENT_CALL;
    }).map((element) => {
      return element.name;
    });
  }

  initializeData(): void {
    this.node.topologyGroupsInfo = _.isEmpty(this.node.topologyGroups) ? '' :
      this.node.topologyGroups.map(group => group.name).join(', ');
  }

  private dataInitialization(selection: any): void {
    if (_.isNil(selection)) { return; }

    const shapes = selection.nodes;
    const connections = selection.edges;
    if (shapes.length === 1 && connections.length === 0 && shapes[0].shapeType === 'node') {
      this.node = shapes[0].dataItem;
      this.initializeData();
      this.getChartsData();
    }
  }
}
